import { useEffect, useState, useCallback } from 'react';
import {
  addFavorite,
  getFavoriteSlugs,
  isFavorited as isFavoritedStore,
  removeFavorite,
  type FavoriteSlug,
  FAVORITES_UPDATED_EVENT,
} from '@/lib/favorites';

interface UseFavorites {
  favorites: FavoriteSlug[];
  isFavorited: (slug: FavoriteSlug) => boolean;
  add: (slug: FavoriteSlug) => void;
  remove: (slug: FavoriteSlug) => void;
  toggle: (slug: FavoriteSlug) => void;
  refresh: () => void;
}

export function useFavorites(): UseFavorites {
  const [favorites, setFavorites] = useState<FavoriteSlug[]>([]);

  // Load from localStorage on mount (client-only)
  useEffect(() => {
    setFavorites(getFavoriteSlugs());
  }, []);

  const refresh = useCallback(() => {
    setFavorites(getFavoriteSlugs());
  }, []);

  const isFavorited = useCallback(
    (slug: FavoriteSlug) => favorites.includes(slug),
    [favorites],
  );

  const add = useCallback(
    (slug: FavoriteSlug) => {
      const updated = addFavorite(slug);
      setFavorites(updated);
    },
    [],
  );

  const remove = useCallback(
    (slug: FavoriteSlug) => {
      const updated = removeFavorite(slug);
      setFavorites(updated);
    },
    [],
  );

  const toggle = useCallback(
    (slug: FavoriteSlug) => {
      if (isFavoritedStore(slug)) {
        const updated = removeFavorite(slug);
        setFavorites(updated);
      } else {
        const updated = addFavorite(slug);
        setFavorites(updated);
      }
    },
    [],
  );

  // Refresh on cross-tab or other component updates
  useEffect(() => {
    const handleUpdate = () => refresh();
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'attain.favorites.v1') refresh();
    };
    window.addEventListener(FAVORITES_UPDATED_EVENT, handleUpdate as EventListener);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleUpdate as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [refresh]);

  return { favorites, isFavorited, add, remove, toggle, refresh };
}

export default useFavorites;

